// stereo-core: port of the DSEpy 1.0.1 set-identification pipeline (stereonet.py:
// kde2d, compute_density_grid, find_principal_poles, classify_point_cloud_js).
// Upstream: Riquelme, Tomás & Abellán, DSEpy (GPL-3.0), commit 7155e3b. The 2D KDE is
// Botev et al. (2010) diffusion estimator. Ported faithfully, including array
// orientation, so results match DSEpy (see test/fixtures/dsepy-reference.json).
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { Vec3, DEG } from "./vec.js";

export type DseProjection = "Equal-angle" | "Equal-area";
export interface DsePole { x: number; y: number; density: number; normal: Vec3; dipdir: number; dip: number }

// ---------- DSE projection helpers ----------
// [DSE-01]
/** f_clar2cart: pole position of a plane. Same as DipField's lower-hemisphere pole plot. */
export function dseClar2cart(dipdir: number, dip: number, p: DseProjection): [number, number] {
  // float operation order copied from numpy so bin edges fall identically
  const ddr = (dipdir * Math.PI) / 180, dr = (dip * Math.PI) / 180;
  const r = p === "Equal-area" ? Math.sqrt(2) * Math.cos(Math.PI / 2 - dr / 2) : Math.tan(dr / 2);
  const om = ((ddr % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  const alfa = om < Math.PI / 2 ? Math.PI / 2 - om + Math.PI : om < Math.PI ? Math.PI - (om - Math.PI / 2)
    : om < (3 * Math.PI) / 2 ? Math.PI / 2 - (om - Math.PI) : (7 * Math.PI) / 2 - om; // o2a
  return [r * Math.cos(alfa), r * Math.sin(alfa)];
}
// [DSE-01]
/** f_pole2vnor_v02: net point -> upward plane normal. */
export function dsePole2vnor(px: number, py: number, p: DseProjection): Vec3 {
  if (px === 0 && py === 0) return [0, 0, 1];
  let avn = 0;
  if (px > 0 && py > 0) avn = Math.atan(py / px) + Math.PI;
  else if (px < 0 && py >= 0) avn = 2 * Math.PI - Math.atan(-py / px);
  else if (px < 0 && py <= 0) avn = Math.atan(py / px);
  else if (px >= 0 && py < 0) avn = Math.PI - Math.atan(-py / px);
  const rho = Math.min(1, Math.hypot(px, py));
  const beta = p === "Equal-area" ? 2 * Math.asin(rho / Math.SQRT2) : 2 * Math.atan(rho);
  const alpha = Math.PI / 2 - beta, nxy = Math.cos(alpha);
  let n: Vec3 = [nxy * Math.cos(avn), nxy * Math.sin(avn), Math.sin(alpha)];
  if (n[2] < 0) n = [-n[0], -n[1], -n[2]];
  return n;
}
/** f_vnorm2clar_v02 for one normal. */
export function dseVnorm2clar(n0: Vec3): { dipdir: number; dip: number } {
  let n = n0; if (n[2] < 0) n = [-n[0], -n[1], -n[2]];
  let dipdir = 0, dip = 0;
  if (n[2] < 1) { let om = Math.atan2(n[1], n[0]); if (om < 0) om += 2 * Math.PI; let a = Math.PI / 2 - om; if (a < 0) a += 2 * Math.PI; dipdir = a; }
  if (n[2] === 0) dip = Math.PI / 2; else dip = Math.atan(Math.hypot(n[0], n[1]) / n[2]);
  return { dipdir: (dipdir * 180) / Math.PI, dip: (dip * 180) / Math.PI };
}

// ---------- FFT / DCT (as numpy, MATLAB-compatible kde2d helpers) ----------
function fftInPlace(re: Float64Array, im: Float64Array, inverse: boolean) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1; for (; j & bit; bit >>= 1) j ^= bit; j ^= bit;
    if (i < j) { [re[i], re[j]] = [re[j], re[i]]; [im[i], im[j]] = [im[j], im[i]]; }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = ((inverse ? 2 : -2) * Math.PI) / len, wr = Math.cos(ang), wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1, ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const a = i + k, b = a + len / 2;
        const tr = re[b] * cr - im[b] * ci, ti = re[b] * ci + im[b] * cr;
        re[b] = re[a] - tr; im[b] = im[a] - ti; re[a] += tr; im[a] += ti;
        const nr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = nr;
      }
    }
  }
  if (inverse) for (let i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
}
type M = Float64Array[]; // M[row][col]
const zeros = (n: number): M => Array.from({ length: n }, () => new Float64Array(n));
const transpose = (a: M): M => { const n = a.length, t = zeros(n); for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) t[j][i] = a[i][j]; return t; };
/** dct1d along axis 0 (each column). */
function dct1d(x: M): M {
  const n = x.length, out = zeros(n);
  const idx: number[] = []; for (let i = 0; i < n; i += 2) idx.push(i); for (let i = n % 2 === 0 ? n - 1 : n - 2; i > 0; i -= 2) idx.push(i);
  const re = new Float64Array(n), im = new Float64Array(n);
  for (let c = 0; c < n; c++) {
    for (let r = 0; r < n; r++) { re[r] = x[idx[r]][c]; im[r] = 0; }
    fftInPlace(re, im, false);
    for (let k = 0; k < n; k++) {
      if (k === 0) { out[k][c] = re[0]; continue; }
      const a = (-k * Math.PI) / (2 * n), wr = 2 * Math.cos(a), wi = 2 * Math.sin(a);
      out[k][c] = wr * re[k] - wi * im[k];
    }
  }
  return out;
}
function idct1d(x: M): M {
  const n = x.length, out = zeros(n), re = new Float64Array(n), im = new Float64Array(n);
  for (let c = 0; c < n; c++) {
    for (let k = 0; k < n; k++) { const a = (k * Math.PI) / (2 * n); re[k] = Math.cos(a) * x[k][c]; im[k] = Math.sin(a) * x[k][c]; }
    fftInPlace(re, im, true);
    for (let i = 0; i < n / 2; i++) out[2 * i][c] = re[i];
    for (let i = 0; i < n / 2; i++) out[2 * i + 1][c] = re[n - 1 - i];
  }
  return out;
}
const dct2d = (d: M) => transpose(dct1d(transpose(dct1d(d))));
const idct2d = (d: M) => idct1d(transpose(idct1d(d)));

/** Brent's method root finder (as scipy.optimize.brentq). Throws if no sign change. */
export function brentq(f: (x: number) => number, a: number, b: number, xtol = 2e-12, rtol = 8.881784197001252e-16, maxiter = 100): number {
  let fa = f(a), fb = f(b);
  if (fa * fb > 0) throw new Error("f(a) and f(b) must have different signs");
  if (fa === 0) return a; if (fb === 0) return b;
  let c = a, fc = fa, d = b - a, e = d;
  for (let it = 0; it < maxiter; it++) {
    if (fb * fc > 0) { c = a; fc = fa; d = b - a; e = d; }
    if (Math.abs(fc) < Math.abs(fb)) { a = b; b = c; c = a; fa = fb; fb = fc; fc = fa; }
    const tol = 2 * rtol * Math.abs(b) + xtol / 2, m = (c - b) / 2;
    if (Math.abs(m) <= tol || fb === 0) return b;
    if (Math.abs(e) >= tol && Math.abs(fa) > Math.abs(fb)) {
      let p: number, q: number; const s = fb / fa;
      if (a === c) { p = 2 * m * s; q = 1 - s; }
      else { const qq = fa / fc, r = fb / fc; p = s * (2 * m * qq * (qq - r) - (b - a) * (r - 1)); q = (qq - 1) * (r - 1) * (s - 1); }
      if (p > 0) q = -q; else p = -p;
      if (2 * p < Math.min(3 * m * q - Math.abs(tol * q), Math.abs(e * q))) { e = d; d = p / q; } else { d = m; e = m; }
    } else { d = m; e = m; }
    a = b; fa = fb;
    b += Math.abs(d) > tol ? d : m > 0 ? tol : -tol;
    fb = f(b);
  }
  return b;
}

// ---------- kde2d (Botev 2010), grid over [-1,1]^2 ----------
export interface Kde2d { n: number; density: M; x: Float64Array; y: Float64Array; bandwidth: [number, number] }
// [DSE-02]
export function kde2d(xs: ArrayLike<number>, ys: ArrayLike<number>, n0 = 256, weights?: ArrayLike<number>): Kde2d {
  const N = xs.length, n = 2 ** Math.ceil(Math.log2(n0));
  const MIN = [-1, -1], scaling = [2, 2];
  // ndhist_matlab: bins[:,0] (x) is the ROW index
  const hist = zeros(n);
  let W = 0; for (let i = 0; i < N; i++) W += weights ? weights[i] : 1;
  for (let i = 0; i < N; i++) {
    const bx = Math.min(n - 1, Math.max(0, Math.floor(((xs[i] - MIN[0]) / scaling[0]) * n)));
    const by = Math.min(n - 1, Math.max(0, Math.floor(((ys[i] - MIN[1]) / scaling[1]) * n)));
    hist[bx][by] += (weights ? weights[i] : 1) / W;
  }
  const a = dct2d(hist);
  const I = new Float64Array(n); for (let k = 0; k < n; k++) I[k] = k * k;
  const A2 = a.map((row) => row.map((v) => v * v));
  const Kf = (s: number) => { let prod = 1; for (let k = 1; k < 2 * s; k += 2) prod *= k; return ((-1) ** s * prod) / Math.sqrt(2 * Math.PI); };
  const psi = (s: [number, number], T: number) => {
    const w = new Float64Array(n); for (let k = 0; k < n; k++) w[k] = Math.exp(-I[k] * Math.PI ** 2 * T) * (k === 0 ? 1 : 0.5);
    let tot = 0;
    for (let i = 0; i < n; i++) {
      const wyi = w[i] * I[i] ** s[1]; if (wyi === 0) continue;
      let row = 0; const A = A2[i];
      for (let j = 0; j < n; j++) row += A[j] * w[j] * I[j] ** s[0];
      tot += wyi * row;
    }
    return (-1) ** (s[0] + s[1]) * tot * Math.PI ** (2 * (s[0] + s[1]));
  };
  const func = (s: [number, number], t: number): number => {
    const ss = s[0] + s[1];
    if (ss <= 4) {
      const sum = func([s[0] + 1, s[1]], t) + func([s[0], s[1] + 1], t);
      const cst = (1 + 1 / 2 ** (ss + 1)) / 3;
      const tv = ((-2 * cst * Kf(s[0]) * Kf(s[1])) / (N * sum)) ** (1 / (2 + ss));
      return psi(s, tv);
    }
    return psi(s, t);
  };
  const evolve = (t: number) => {
    const sum = func([0, 2], t) + func([2, 0], t) + 2 * func([1, 1], t);
    const tv = (2 * Math.PI * N * sum) ** (-1 / 3);
    return (t - tv) / tv;
  };
  let tStar: number;
  try { tStar = brentq(evolve, 0, 0.1); if (!isFinite(tStar)) tStar = 0.01; } catch { tStar = 0.01; }
  const p02 = func([0, 2], tStar), p20 = func([2, 0], tStar), p11 = func([1, 1], tStar);
  const den = 4 * Math.PI * N * (p11 + Math.sqrt(Math.max(0, p20 * p02)));
  let tx: number, ty: number;
  if (den !== 0 && p02 > 0 && p20 > 0) {
    ty = (p02 ** 0.75 / (den * p20 ** 0.75)) ** (1 / 3);
    tx = (p20 ** 0.75 / (den * p02 ** 0.75)) ** (1 / 3);
  } else { tx = ty = tStar; }
  const at = zeros(n);
  for (let kx = 0; kx < n; kx++) for (let ky = 0; ky < n; ky++)
    at[kx][ky] = Math.exp((-kx * kx * Math.PI ** 2 * tx) / 2) * Math.exp((-ky * ky * Math.PI ** 2 * ty) / 2) * a[kx][ky];
  const dens = idct2d(at), f = (n * n) / (scaling[0] * scaling[1]);
  for (const row of dens) for (let j = 0; j < n; j++) row[j] *= f;
  const x = new Float64Array(n), y = new Float64Array(n);
  for (let k = 0; k < n; k++) { x[k] = -1 + (2 * k) / (n - 1); y[k] = -1 + (2 * k) / (n - 1); }
  return { n, density: dens, x, y, bandwidth: [Math.sqrt(tx) * 2, Math.sqrt(ty) * 2] };
}

// [DSE-03]
/** compute_density_grid: Z[r][c] at (x = x[c], y = y[r]); NaN outside / below 0.5 %; max = 100. */
export function dseDensityGrid(xs: ArrayLike<number>, ys: ArrayLike<number>, gridRes = 256, weights?: ArrayLike<number>) {
  const k = kde2d(xs, ys, gridRes, weights), n = k.n, Z = zeros(n);
  let mx = -Infinity;
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    const v = Math.hypot(k.x[c], k.y[r]) <= 1 ? k.density[r][c] : NaN;
    Z[r][c] = v < 1e-3 ? NaN : v; if (Z[r][c] > mx) mx = Z[r][c];
  }
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    let v = Z[r][c]; if (mx > 0) v = (v / mx) * 100; Z[r][c] = v < 0.5 ? NaN : v;
  }
  return { X: k.x, Y: k.y, Z, bandwidth: k.bandwidth };
}

// [DSE-04]
/** find_principal_poles. `minPeakPct` (DipField addition, 0 = upstream) drops weak
 *  candidates BEFORE the separation test so they cannot take a slot. */
export function dseFindPrincipalPoles(g: { X: Float64Array; Y: Float64Array; Z: M }, p: DseProjection,
  minAngleDeg = 30, maxPoles = 5, minPeakPct = 0): DsePole[] {
  const n = g.Z.length, Zc = g.Z.map((r) => r.map((v) => (isNaN(v) ? 0 : v)));
  const cand: DsePole[] = [];
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    const v = Zc[r][c]; if (!(v > 0.5)) continue;
    let isMax = true;
    for (let dr = -1; dr <= 1 && isMax; dr++) for (let dc = -1; dc <= 1; dc++) {
      const rr = r + dr, cc = c + dc; if (rr < 0 || cc < 0 || rr >= n || cc >= n) continue;
      if (Zc[rr][cc] > v) { isMax = false; break; }
    }
    if (!isMax) continue;
    const normal = dsePole2vnor(g.X[c], g.Y[r], p), cl = dseVnorm2clar(normal);
    cand.push({ x: g.X[c], y: g.Y[r], density: v, normal, ...cl });
  }
  cand.sort((a, b) => b.density - a.density); // stable, like Python's sort
  const out: DsePole[] = [];
  for (const q of cand) {
    if (q.density < minPeakPct) continue;
    const ok = out.every((a) => Math.acos(Math.min(1, Math.abs(q.normal[0] * a.normal[0] + q.normal[1] * a.normal[1] + q.normal[2] * a.normal[2]))) / DEG >= minAngleDeg);
    if (ok) { out.push(q); if (out.length >= maxPoles) break; }
  }
  return out;
}

// [DSE-05]
/** classify_point_cloud_js: nearest principal pole within the cone (0 = none). */
export function dseClassify(normals: Vec3[], poles: DsePole[], coneDeg = 30): Int32Array {
  const out = new Int32Array(normals.length);
  normals.forEach((v0, i) => {
    const L = Math.hypot(v0[0], v0[1], v0[2]) || 1, v: Vec3 = [v0[0] / L, v0[1] / L, v0[2] / L];
    let best = Infinity, bi = 0;
    poles.forEach((p, k) => {
      const q = p.normal, Lq = Math.hypot(q[0], q[1], q[2]) || 1;
      const ang = Math.acos(Math.min(1, Math.abs((v[0] * q[0] + v[1] * q[1] + v[2] * q[2]) / Lq))) / DEG;
      if (ang < best) { best = ang; bi = k + 1; } // argmin: first minimum wins
    });
    out[i] = best <= coneDeg ? bi : 0;
  });
  return out;
}

export interface DseOptions { projection?: DseProjection; gridRes?: number; minAngleDeg?: number; maxSets?: number; coneDeg?: number; minPeakPct?: number; weights?: ArrayLike<number> }
/** Full pipeline on planes (dip direction / dip). */
export function dseIdentifySets(dipdir: ArrayLike<number>, dip: ArrayLike<number>, o: DseOptions = {}) {
  const P = o.projection ?? "Equal-area", n = dip.length;
  const xs = new Float64Array(n), ys = new Float64Array(n), normals: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    [xs[i], ys[i]] = dseClar2cart(dipdir[i], dip[i], P);
    const dd = dipdir[i] * DEG, d = dip[i] * DEG; // upward normal, E-N-Up
    normals.push([Math.sin(d) * Math.sin(dd), Math.sin(d) * Math.cos(dd), Math.cos(d)]);
  }
  const g = dseDensityGrid(xs, ys, o.gridRes ?? 256, o.weights);
  const poles = dseFindPrincipalPoles(g, P, o.minAngleDeg ?? 30, o.maxSets ?? 5, o.minPeakPct ?? 0);
  return { poles, assignment: dseClassify(normals, poles, o.coneDeg ?? 30), bandwidth: g.bandwidth };
}
